const FRAME_RESIZE_MESSAGE_TYPE = "travelstat:resize";

declare global {
  interface Window {
    travelstatSendHeight?: () => void;
  }
}

let cleanupFrameHeightMessenger: (() => void) | null = null;

const isInsideFrame = () => window.parent !== window;

const getDocumentHeight = () => {
  const { body, documentElement } = document;

  return Math.ceil(
    Math.max(
      documentElement.scrollHeight,
      documentElement.offsetHeight,
      documentElement.clientHeight,
      body?.scrollHeight ?? 0,
      body?.offsetHeight ?? 0,
      body?.clientHeight ?? 0
    )
  );
};

const getParentOrigin = () => {
  if (!document.referrer) return "*";

  try {
    return new URL(document.referrer).origin;
  } catch {
    return "*";
  }
};

export const setupFrameHeightMessenger = () => {
  if (cleanupFrameHeightMessenger) return cleanupFrameHeightMessenger;

  if (!isInsideFrame()) {
    window.travelstatSendHeight = undefined;
    return undefined;
  }

  const targetOrigin = getParentOrigin();
  let animationFrameId: number | null = null;
  let lastSentHeight = 0;
  const timeoutIds: number[] = [];

  const sendHeight = () => {
    animationFrameId = null;
    const height = getDocumentHeight();

    if (height === lastSentHeight) return;
    lastSentHeight = height;

    window.parent.postMessage(
      {
        type: FRAME_RESIZE_MESSAGE_TYPE,
        height,
      },
      targetOrigin
    );
  };

  const scheduleSendHeight = () => {
    if (animationFrameId !== null) return;
    animationFrameId = window.requestAnimationFrame(sendHeight);
  };

  window.travelstatSendHeight = scheduleSendHeight;

  window.addEventListener("load", scheduleSendHeight);
  window.addEventListener("resize", scheduleSendHeight);
  window.addEventListener("orientationchange", scheduleSendHeight);

  const resizeObserver = window.ResizeObserver ? new ResizeObserver(scheduleSendHeight) : null;
  resizeObserver?.observe(document.documentElement);
  if (document.body) resizeObserver?.observe(document.body);

  const root = document.getElementById("root");
  if (root) resizeObserver?.observe(root);

  const mutationObserver = window.MutationObserver
    ? new MutationObserver(scheduleSendHeight)
    : null;

  mutationObserver?.observe(document.documentElement, {
    attributes: true,
    childList: true,
    subtree: true,
  });

  scheduleSendHeight();
  timeoutIds.push(window.setTimeout(scheduleSendHeight, 100));
  timeoutIds.push(window.setTimeout(scheduleSendHeight, 500));

  cleanupFrameHeightMessenger = () => {
    if (animationFrameId !== null) {
      window.cancelAnimationFrame(animationFrameId);
      animationFrameId = null;
    }

    window.removeEventListener("load", scheduleSendHeight);
    window.removeEventListener("resize", scheduleSendHeight);
    window.removeEventListener("orientationchange", scheduleSendHeight);
    resizeObserver?.disconnect();
    mutationObserver?.disconnect();
    timeoutIds.forEach((timeoutId) => window.clearTimeout(timeoutId));

    if (window.travelstatSendHeight === scheduleSendHeight) {
      window.travelstatSendHeight = undefined;
    }

    cleanupFrameHeightMessenger = null;
  };

  return cleanupFrameHeightMessenger;
};
